import { Box, Checkbox, Chip, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DoseInfo } from '~/components/InfoTooltip/DoseInfo';
import { getModalityIcon, Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import transformDoseMatrixToDoseData from '~/utils/transforms/doseMatrixTransforms';
import { ActiveItem } from '../../useActiveItems';
import { SelectedDatasets } from '../LoadedPatientDataTree';

interface DoseLeafProps {
    dose: DoseMatrix;
    image: Image3D;
    structureSet: StructureSet;
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onSelect: React.Dispatch<React.SetStateAction<SelectedDatasets>>;
}

const DoseLeaf = ({
    dose,
    image,
    structureSet,
    selectedDatasets,
    activeItems,
    onSelect,
}: DoseLeafProps) => {
    const isDoseActive = activeItems.some(
        (item) => item.dicomUid === dose.dicomUid
    );
    const isDoseSelected = selectedDatasets?.dose?.dicomUid === dose.dicomUid;

    const handleSelect = () => {
        if (isDoseSelected) {
            onSelect((prev) => ({
                ...prev,
                dose: undefined,
            }));
        } else {
            onSelect((state) => ({
                image,
                structureSet,
                dose,
                registration: state.registration,
            }));
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            py={0.5}
            key={dose.dicomUid}
            pr={1}
            pl={5}
            onClick={handleSelect}
            sx={(theme) => ({
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
                ...(isDoseActive && {
                    backgroundColor: 'rgba(0, 165, 185, 0.05)',
                }),
            })}
        >
            <Box
                display="grid"
                gridTemplateColumns="auto auto auto 1fr auto"
                alignItems="center"
                gap={1}
            >
                <Checkbox
                    disableRipple
                    size="small"
                    color="primary"
                    checked={isDoseSelected}
                    sx={{
                        '&': {
                            padding: 0,
                        },
                    }}
                />
                <Box>{getModalityIcon(Modality.RTDOSE, 12)}</Box>

                <Typography display="flex" alignItems="center">
                    {Modality.RTDOSE}
                </Typography>
                <Typography
                    display="flex"
                    alignItems="center"
                    variant="body2"
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {dose.plan?.label ?? 'Dose'}
                </Typography>

                <Typography color="primary">
                    <DoseInfo dose={transformDoseMatrixToDoseData(dose, 1)} />
                </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
                {isDoseActive && (
                    <Chip
                        variant="outlined"
                        size="small"
                        sx={{
                            fontSize: 10,
                            height: 20,
                            padding: 0.5,
                            lineHeight: 0.9,
                        }}
                        color="success"
                        label={<FormattedMessage id="active" />}
                    />
                )}
            </Box>
        </Box>
    );
};

export default DoseLeaf;
